import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Button, Paper, useTheme } from '@mui/material';
import { useRevitData } from './shared/providers/RevitDataContext';
import { useNavigation, useSelectedOption } from './shared/lib';
import { isGrpcRuntimeAvailable } from './shared/api/grpc/transport';
import { useAuthStore } from './shared/stores/useAuthStore';
import { pipeexRuntimeConfig } from './shared/config/pipeexRuntimeConfig';
import { getE2eBootstrapConfig } from './shared/config/e2eBootstrap';
import { applyE2eBootstrap } from './shared/config/applyE2eBootstrap';
import { workflowIncludesIsoOnlySetup } from './shared/config/workflowUi';
import { AUTH_SESSION_CLEARED_EVENT } from './shared/lib/authSession';
import { flushDurableStateWrites } from './shared/lib/durableState/durableState';
import { useConfigurationStore } from './shared/stores/useConfigurationStore';
import { useUIStore } from './shared/stores/useUIStore';
import { useActiveProjectKey } from './shared/hooks/ActiveDocumentProvider';
import {
  OperationProgressPlaceholder,
  type OperationProgressModel,
} from './shared/ui/progress';
import IntegrationDiagnosticsPanel from './shared/ui/feedback/IntegrationDiagnosticsPanel';
import { markLiveWizardMappingHandoffIntent } from './shared/lib/workflowResumeIntent';
import { useConvertCurrentSettings } from './features/workflow-setup/hooks/useConvertCurrentSettings';
import { markMappingProfileProjectTrust } from './features/mapping-configuration/lib/mappingProfileProjectTrust';
import RuntimeModeBadge from './shared/ui/feedback/RuntimeModeBadge';
import PluginUpdatePrompt from './shared/ui/feedback/PluginUpdatePrompt';
import PluginUpdateRequiredModal from './shared/ui/feedback/PluginUpdateRequiredModal';
import RuntimeVersionInfo from './shared/ui/feedback/RuntimeVersionInfo';
import { DeveloperDock } from './testRecords/ui/DeveloperDock';
import { registerRecorderArchiveDownloader } from './testRecords/integration/recorderArchiveCollector';
import { isRecorderUiEnabled } from './testRecords/recording/recorderBuildPolicy';
import { installRecorderUiCapture } from './testRecords/integration/uiStepCapturer';
import { LoginPage } from './features/auth/index';
import { MenuPage as MenuPageRedux } from './features/menu';
import {
  AdditionalCategoriesPage,
  BoltConnectionPage,
  ConnectorBindingPage,
  ProcessPropertiesPage,
  WelcomePage,
} from './features/workflow-setup';
import {
  MappingConfigurationPage,
  useMappingStartupResume,
} from './features/mapping-configuration';
import { QuickFillPage as QuickFillPageRedux } from './features/quick-fill';
import { PcfValidationPage } from './features/pcf-validation';
import { getUnifiedScreenFrame } from './theme/theme';
import { requestHostWindowClose } from './shared/lib/utils/windowSizing';
// Session tracking - DISABLED to prevent WebView2 freeze
// The synchronous storage access was causing WebView2 to hang
const SESSION_INIT_ENABLED = false;
function initSession(): void {
  if (!SESSION_INIT_ENABLED) {
    return;
  }
  // Session init code removed - was causing WebView2 freeze
}

// Loading/Error screen component that uses theme
interface LoadingScreenProps {
  error?: string | null;
  onRetry?: () => void;
  mode?: 'auth' | 'revit';
}

const buildLoadingModel = (
  error: string | null | undefined,
  mode: LoadingScreenProps['mode'] = 'revit'
): OperationProgressModel => {
  if (error) {
    return {
      title: 'Failed to load workspace',
      message: 'PipeEX could not complete the startup operation',
      detail: error,
      status: 'error',
      percent: null,
      phase: mode === 'auth' ? 'Session restore failed' : 'Workspace load failed',
      locked: false,
    };
  }

  if (mode === 'auth') {
    return {
      title: 'Restoring PipeEX session',
      message: 'Checking the saved login and runtime session',
      detail: 'The workspace will open as soon as your session is ready.',
      status: 'pending',
      percent: null,
      phase: 'Checking session',
      locked: true,
    };
  }

  return {
    title: 'Loading Revit workspace',
    message: 'Preparing document state and cached project data',
    detail: 'PipeEX is loading the active Revit model and saved project data.',
    status: 'running',
    percent: null,
    phase: 'Opening workspace',
    locked: true,
  };
};

const LoadingScreen: React.FC<LoadingScreenProps> = ({ error, onRetry, mode = 'revit' }) => {
  const theme = useTheme();
  const screenFrame = useMemo(() => getUnifiedScreenFrame(theme), [theme]);
  const loadingModel = buildLoadingModel(error, mode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'background.default',
        color: 'text.primary',
        p: 0,
        textAlign: 'center',
      }}
    >
      <Paper
        component="section"
        aria-label={error ? 'Error loading PipeEX workspace' : 'Loading PipeEX workspace'}
        elevation={0}
        sx={{
          ...screenFrame,
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          p: { xs: 1.5, md: 2 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: (theme) => theme.spacing(1.5),
            right: (theme) => theme.spacing(1.5),
          }}
        >
          <RuntimeModeBadge />
        </Box>
        <OperationProgressPlaceholder
          model={loadingModel}
          sx={{ maxWidth: 760, maxHeight: 'none', height: 'auto', px: 0, py: 0 }}
        />
        {error && onRetry ? (
          <Button variant="contained" color="primary" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </Paper>
    </Box>
  );
};

// Initialize session on module load
initSession();

const AppContent: React.FC = () => {
  const theme = useTheme();
  const { currentScreen, navigateTo, goBack } = useNavigation();
  const screenFrame = useMemo(() => getUnifiedScreenFrame(theme), [theme]);
  const restoreWizardStepDefaults = useConfigurationStore((state) => state.restoreWizardStepDefaults);
  const resetProjectScopedConfigurationState = useConfigurationStore(
    (state) => state.resetProjectScopedConfigurationState
  );
  const resetProjectScopedRuntimeState = useUIStore((state) => state.resetProjectScopedRuntimeState);
  const reconcileProjectScopedRuntimeState = useUIStore(
    (state) => state.reconcileProjectScopedRuntimeState
  );
  const selectedOption = useSelectedOption();
  const { isLoaded, isLoading, loadRevitApiData, refreshData, error } = useRevitData();
  const activeProjectKey = useActiveProjectKey();
  const {
    hydrated: authHydrated,
    user,
    accessToken,
    hydrate,
    syncFromStorage,
    releaseRuntimeSession,
  } = useAuthStore();
  const initialLoadDone = useRef(false);
  const observedProjectKeyRef = useRef<string | null>(null);
  const projectKeyInitializedRef = useRef(false);
  const e2eBootstrapApplied = useRef(false);
  const runtimeActive = isGrpcRuntimeAvailable();
  const skipAuthInCurrentMockMode =
    pipeexRuntimeConfig.useMockData &&
    (pipeexRuntimeConfig.skipAuthInMockMode || isGrpcRuntimeAvailable());
  // e2e=1 query param bypasses auth only in dev or explicit Playwright e2e builds.
  const e2eBypass = useMemo(() => {
    if (
      process.env.NODE_ENV !== 'development' &&
      process.env.REACT_APP_PIPEEX_E2E_BYPASS !== '1'
    ) {
      return false;
    }

    return new URLSearchParams(window.location.search).get('e2e') === '1';
  }, []);
  const e2eBootstrap = useMemo(
    () =>
      typeof window === 'undefined'
        ? null
        : getE2eBootstrapConfig(window.location.search, process.env.NODE_ENV),
    []
  );
  const showIntegrationDiagnostics = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return new URLSearchParams(window.location.search).get('debugIntegrationPanel') === '1';
  }, []);
  const authRequired =
    !e2eBypass && (
      runtimeActive ||
      (pipeexRuntimeConfig.useMockData && !skipAuthInCurrentMockMode)
    );
  const isAuthenticated = !!user;
  const authSatisfied = !authRequired || isAuthenticated;
  const handleRetry = useCallback(() => {
    initialLoadDone.current = false;
    loadRevitApiData();
  }, [loadRevitApiData]);

  const { handleConvert: handleConvertCurrentSettings, isConverting: isConvertingSettings } = useConvertCurrentSettings({
      userId: user?.id,
      activeProjectKey,
      navigateTo,
      bindProfileToProject: useCallback((profileKey: string, origin: 'legacy-conversion') => {
        markMappingProfileProjectTrust({ userId: user?.id, projectKey: activeProjectKey, profileKey, settingsType: 1, source: 'live-flow', setupReadinessPolicy: origin === 'legacy-conversion' ? 'legacy-conversion-bypass' : undefined });
      }, [activeProjectKey, user?.id]),
    });

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // Only local builds wire archive collection and global recording listeners.
  useEffect(() => {
    if (!isRecorderUiEnabled()) return;
    registerRecorderArchiveDownloader();
    installRecorderUiCapture();
  }, []);

  useEffect(() => {
    const handler = () => {
      syncFromStorage();
    };

    window.addEventListener(AUTH_SESSION_CLEARED_EVENT, handler);
    return () => {
      window.removeEventListener(AUTH_SESSION_CLEARED_EVENT, handler);
    };
  }, [syncFromStorage]);

  // Close handler. window.close() is a no-op in the main WebView2 frame, so the
  // web app posts a closeWindow message; the WPF host hides the tool window.
  const handleCloseApp = useCallback(async () => {
    await flushDurableStateWrites();
    await releaseRuntimeSession();
    if (!requestHostWindowClose()) {
      window.close();
    }
  }, [releaseRuntimeSession]);

  useEffect(() => {
    if (!user || !accessToken) {
      return;
    }

    const handlePageHide = () => {
      void useAuthStore.getState().releaseRuntimeSession({ preferKeepalive: true });
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [accessToken, user]);

  // Load Revit API data when the app starts (ONCE ONLY)
  useEffect(() => {
    if (!authSatisfied) {
      return;
    }

    if (!initialLoadDone.current && !isLoading) {
      initialLoadDone.current = true;
      loadRevitApiData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSatisfied]); // Load once after auth gating succeeds.

  useEffect(() => {
    if (!authSatisfied || !activeProjectKey) {
      return;
    }

    // First sight of a document after a reload is not a project switch: the
    // restored state has to be asked who it belongs to. Discarding it blindly
    // would drop a RecoveryRequired lock and the undo checkpoint that clears it.
    if (!projectKeyInitializedRef.current) {
      projectKeyInitializedRef.current = true;
      observedProjectKeyRef.current = activeProjectKey;
      if (reconcileProjectScopedRuntimeState(activeProjectKey).kind === 'reset') {
        resetProjectScopedConfigurationState();
      }
      return;
    }

    if (observedProjectKeyRef.current === activeProjectKey) {
      return;
    }

    // An observed change of document is proof, so this one resets outright.
    observedProjectKeyRef.current = activeProjectKey;
    resetProjectScopedRuntimeState();
    resetProjectScopedConfigurationState();
    void refreshData();
  }, [
    activeProjectKey,
    authSatisfied,
    reconcileProjectScopedRuntimeState,
    refreshData,
    resetProjectScopedConfigurationState,
    resetProjectScopedRuntimeState,
  ]);

  useMappingStartupResume({
    authSatisfied,
    activeUserId: user?.id ?? null,
    activeProjectKey,
    currentScreen,
    navigateTo,
  });

  useEffect(() => {
    if (!authSatisfied || !e2eBootstrap || e2eBootstrapApplied.current) {
      return;
    }

    applyE2eBootstrap(e2eBootstrap);
    e2eBootstrapApplied.current = true;
  }, [authSatisfied, e2eBootstrap]);

  useEffect(() => {
    if (currentScreen === 'boltConnection' || currentScreen === 'additionalCategories' || currentScreen === 'processProperties') {
      restoreWizardStepDefaults(currentScreen);
    }
  }, [currentScreen, restoreWizardStepDefaults]);

  // Revit-dependent screens require gRPC or WebView2 runtime
  const revitScreens = new Set([
    'connectorBinding',
    'quickFillProperties',
    'boltConnection',
    'processProperties',
    'additionalCategories',
    'mappingConfiguration',
    'pcfValidation',
  ]);

  // Render the appropriate page based on the current screen
  const renderCurrentPage = () => {
    // Block Revit-dependent pages when no runtime connection
    if (revitScreens.has(currentScreen) && !runtimeActive) {
      return (
        <Box
          component="main"
          aria-label="Revit connection required"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            p: 0,
            textAlign: 'center',
          }}
        >
          <Paper
            component="section"
            elevation={0}
            data-testid="revit-connection-required"
            sx={{
              ...screenFrame,
              width: '100%',
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              p: 3,
              overflow: 'hidden',
            }}
          >
            <Box role="alert" sx={{ display: 'grid', justifyItems: 'center', gap: 2 }}>
              <Box sx={{ fontSize: '1.2rem', fontWeight: 500, color: 'warning.main' }}>
                Revit connection required
              </Box>
              <Box sx={{ color: 'text.secondary', maxWidth: 400 }}>
                This page requires an active Revit connection. Open PipeEX from within Revit.
              </Box>
            </Box>
            <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => navigateTo('welcome')}>
              Back to home
            </Button>
          </Paper>
        </Box>
      );
    }

    switch (currentScreen) {
      case 'welcome':
        return <WelcomePage handleContinue={() => navigateTo('menu')} onConvertCurrentSettings={handleConvertCurrentSettings} isConverting={isConvertingSettings} />;
      case 'menu':
        return <MenuPageRedux onContinueClick={() => navigateTo('connectorBinding')} />;
      case 'connectorBinding':
        return <ConnectorBindingPage
          onGoBack={goBack}
          onContinue={() => navigateTo('quickFillProperties')}
        />;
      case 'quickFillProperties':
        return <QuickFillPageRedux
          onGoBack={goBack}
          onContinue={() => navigateTo(workflowIncludesIsoOnlySetup(selectedOption) ? 'boltConnection' : 'processProperties')}
        />;
      case 'boltConnection':
        return <BoltConnectionPage
          onGoBack={goBack}
          onContinue={() => {
            if (selectedOption === 'Iso Drawings Generation') {
              navigateTo('additionalCategories');
            } else {
              navigateTo('processProperties');
            }
          }}
        />;
      case 'processProperties':
        return <ProcessPropertiesPage
          onGoBack={goBack}
          onContinue={() => {
            markLiveWizardMappingHandoffIntent();
            navigateTo('mappingConfiguration');
          }}
        />;
      case 'additionalCategories':
        return <AdditionalCategoriesPage
          onGoBack={goBack}
          onContinue={() => {
            if (selectedOption === 'Iso Drawings Generation') {
              navigateTo('processProperties');
            } else {
              markLiveWizardMappingHandoffIntent();
              navigateTo('mappingConfiguration');
            }
          }}
        />;
      case 'mappingConfiguration':
        return <MappingConfigurationPage
          activeUserId={user?.id ?? null}
          onGoBack={goBack}
          onContinue={handleCloseApp}
        />;
      case 'pcfValidation':
        return <PcfValidationPage onGoBack={goBack} />;
      default:
        return <WelcomePage handleContinue={() => navigateTo('menu')} onConvertCurrentSettings={handleConvertCurrentSettings} isConverting={isConvertingSettings} />;
    }
  };

  // Show login gate in real runtime modes before existing app flow.
  if (authRequired && authHydrated && !isAuthenticated) {
    return <LoginPage />;
  }

  if (authRequired && !authHydrated) {
    return <LoadingScreen mode="auth" />;
  }

  // Show loading/error screen if data is not loaded after auth pass.
  const showLoadingScreen = authSatisfied && (!isLoaded || !!error);

  return showLoadingScreen ? (
    <LoadingScreen error={error} onRetry={handleRetry} />
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {showIntegrationDiagnostics ? <IntegrationDiagnosticsPanel /> : null}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {renderCurrentPage()}
      </Box>
      <RuntimeVersionInfo />
      <PluginUpdatePrompt />
      <PluginUpdateRequiredModal />
      <DeveloperDock />
    </Box>
  );
};

export default AppContent;
